/**
 * 한국/해외 지역 판별 유틸리티
 *
 * - 네이티브 앱: GPS 좌표로 한국 영역 판별
 * - 웹 브라우저: timezone + language 기반 판별
 * - fallback: 한국(카카오맵) 기본
 */

// 한국 영역 바운딩 박스 (대략적 범위)
const KOREA_BOUNDS = {
    minLat: 33.0,
    maxLat: 38.6,
    minLng: 124.5,
    maxLng: 132.0,
};

export type MapProvider = 'kakao' | 'google';

/**
 * 좌표가 한국 영역 내인지 판별
 *
 * 바운딩 박스는 동쪽 섬(울릉도 130.9·독도 131.9)을 포함하려고 maxLng=132 까지 잡지만,
 * 그 박스는 규슈/서일본(후쿠오카 33.6,130.4·기타큐슈 33.9,130.9)도 같이 삼킨다.
 * → 남동쪽(저위도+동경) 코너를 도려내 일본 규슈를 해외로 분류한다.
 *   유지: 부산(35.1,129.0)·제주(33.4,126.5)·울릉도/독도(위도 37+)
 *   제외: 후쿠오카·기타큐슈 등 규슈 (위도 35 미만 & 경도 129.7 초과)
 */
export const isInKorea = (lat: number, lng: number): boolean => {
    const inBox = (
        lat >= KOREA_BOUNDS.minLat &&
        lat <= KOREA_BOUNDS.maxLat &&
        lng >= KOREA_BOUNDS.minLng &&
        lng <= KOREA_BOUNDS.maxLng
    );
    if (!inBox) return false;
    // 규슈/서일본 carve-out — 부산(35.1)보다 남쪽이면서 부산(129.0)보다 동쪽
    if (lat < 35.0 && lng > 129.7) return false;
    return true;
};

/**
 * 브라우저 정보 기반 한국 판별
 * - timezone이 Asia/Seoul이면 한국
 * - navigator.language가 ko로 시작하면 한국
 * - 둘 다 아니면 해외
 */
export const isKoreaByBrowser = (): boolean => {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timezone === 'Asia/Seoul') return true;

        const lang = navigator.language || '';
        if (lang.startsWith('ko')) return true;

        return false;
    } catch {
        return true; // 판별 불가 시 한국 기본
    }
};

/**
 * 사용할 지도 프로바이더 결정
 *
 * 좌표가 있으면 좌표 기준으로 판단 (한국 좌표 → 카카오맵, 해외 좌표 → Leaflet)
 * 좌표가 없으면 브라우저 환경 기준
 *
 * @param coords 모임 장소 좌표
 * @returns 'kakao' | 'google'
 */
export const getMapProvider = (coords?: { lat: number; lng: number } | null): MapProvider => {
    // 좌표가 있으면 좌표 기준으로 판단
    if (coords && coords.lat !== 0 && coords.lng !== 0) {
        return isInKorea(coords.lat, coords.lng) ? 'kakao' : 'google';
    }

    // 좌표 없으면 브라우저 환경 기준
    return isKoreaByBrowser() ? 'kakao' : 'google';
};
